import {
  GeckoAssetModel,
  GeckoEntry,
  RolodexEntryPutJson,
  RolodexEntryTagsType,
  RolodexEntryType,
} from 'types'

import { getExpectedFfsAssetModelSnapshotId } from './ffsIds'

/**
 * The slice of `GeckoAssetModel` the snapshot builder actually needs.
 * Structural so tests can pass a minimal literal instead of round-tripping
 * through `GeckoAssetModel.CreateFromJSON`.
 */
export type SnapshotSourceAssetModel = Pick<GeckoAssetModel, 'raw' | 'tags'>

/**
 * Builds the Rolodex put payload for a ticket's asset-model snapshot from
 * the unit's live asset model.
 *
 * Invariants the rest of the snapshot design leans on:
 *  - `data` is the live entry's plain-JSON `raw.data`, copied verbatim.
 *    NEVER serialize the parsed `data.components` -- it's a `Map` (rebuilt
 *    in the `GeckoAssetModel` constructor) and `JSON.stringify` of a Map
 *    yields `{}`. `raw.data` is the untouched Record source of truth.
 *  - `type` is `ASSET_MODEL` so the entry parses back into a
 *    `GeckoAssetModel` AND so `FeatureSelectionPage`'s hardcoded
 *    `type: ASSET_MODEL` put round-trips it unchanged.
 *  - The `unit` tag is deliberately OMITTED: every live consumer (details
 *    page, `useLibraAssetModelData`, FFS hooks) queries `ASSET_MODEL` by
 *    unit tag, so a unit-less snapshot is invisible to all of them.
 *  - The `libra-ticket` marker tag records the owning ticket. It survives
 *    editor saves (`regenerate*AssetModel` passes `tags` through from
 *    `raw`) and is the affordance for future write-back / staleness /
 *    cleanup work.
 */
export function buildFfsAssetModelSnapshotEntry({
  ticketId,
  liveAssetModel,
}: {
  ticketId: string
  liveAssetModel: SnapshotSourceAssetModel
}): RolodexEntryPutJson {
  const tags: RolodexEntryTagsType = {
    [RolodexEntryType.LIBRA_TICKET]: ticketId,
  }
  const organization = liveAssetModel.tags[RolodexEntryType.ORGANIZATION]
  const site = liveAssetModel.tags[RolodexEntryType.SITE]
  if (organization !== undefined) {
    tags[RolodexEntryType.ORGANIZATION] = organization
  }
  if (site !== undefined) {
    tags[RolodexEntryType.SITE] = site
  }
  return {
    id: getExpectedFfsAssetModelSnapshotId({ ticketId }),
    type: RolodexEntryType.ASSET_MODEL,
    data: liveAssetModel.raw.data,
    tags,
  }
}

export interface ResolvedTicketAssetModel {
  assetModel: GeckoAssetModel | undefined
  /** True when `assetModel` is the ticket's snapshot (vs the live model). */
  isSnapshot: boolean
}

/**
 * Picks the asset model a ticket should read: the snapshot when one exists,
 * else the live model. The live fallback preserves the deterministic
 * id-sorted pick both FFS fetch paths used before snapshots existed, so
 * legacy/non-snapshot tickets bind to exactly the same entry as before.
 */
export function resolveTicketAssetModel({
  snapshotEntries,
  liveEntries,
}: {
  snapshotEntries: ReadonlyArray<GeckoEntry>
  liveEntries: ReadonlyArray<GeckoEntry>
}): ResolvedTicketAssetModel {
  const snapshot = snapshotEntries[0] as GeckoAssetModel | undefined
  if (snapshot) {
    return { assetModel: snapshot, isSnapshot: true }
  }
  if (liveEntries.length === 0) {
    return { assetModel: undefined, isSnapshot: false }
  }
  const sorted = [...liveEntries].sort((a, b) => a.id.localeCompare(b.id))
  if (sorted.length > 1) {
    console.warn(
      `[Libra FFS] Unit has ${sorted.length} asset models; using "${sorted[0].id}".`
    )
  }
  return {
    assetModel: sorted[0] as GeckoAssetModel,
    isSnapshot: false,
  }
}
